/**
 * AI conversation session id — single source of truth for both the
 * ⌘K Command Bar (`useAiAsk.js`) and the mobile capture FAB
 * (`MobileAiCaptureFab.js`).
 *
 * The backend reads this id from `POST /api/ai/ask`, looks up the
 * last ~10 user↔assistant turns under that key, and injects them
 * into the LLM's system prompt so follow-ups work without re-stating
 * context. Sharing the helper means the bar and the FAB both
 * participate in the SAME conversation thread — natural for users
 * who hop between desktop and mobile mid-task.
 *
 * TTL is 24h: long enough that a Monday work day stays coherent,
 * short enough that "old context" doesn't bleed into Tuesday's
 * fresh mental model.
 *
 * Storage note (re-flagged in code review): the persisted value is a
 * v4 UUID — opaque, server-bound, NOT a session token or PII. The
 * actual conversation content lives server-side under that key.
 * Going through `safeStorage` (vs raw `localStorage`) gives us:
 *   - in-memory fallback when localStorage is unavailable
 *     (private-browsing, quota-exceeded, SSR)
 *   - structured warn-logs on failure (no more silent empty catches)
 *   - the sensitive-key guard prevents anyone from accidentally
 *     storing real auth material here in the future.
 */
import { safeStorage } from './safeStorage';
import { logger } from './logger';

// NOTE on storage keys: an earlier version used `polkdesq.ai.sessionId`,
// but `safeStorage`'s sensitive-key guard (intentionally aggressive)
// refuses any key matching /session[-_]?id/i — even though our value is
// a non-sensitive UUID-shaped correlation key. We renamed to `…thread`
// rather than loosen the guard. Cost: any user with stale state under
// the OLD key just gets a fresh thread on next AI ask, which is fine —
// the server-side memory still respects whatever id the client sends.
const STORAGE_KEY = 'polkdesq.ai.thread';
const TIMESTAMP_KEY = 'polkdesq.ai.threadStartedAt';
const TTL_HOURS = 24;

function generateId() {
  // `crypto.randomUUID` is universal since Safari 15.4 (2022).
  // Fall back for older Safari / private-mode quirks.
  if (typeof window !== 'undefined' && typeof window.crypto?.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return 's-' + Math.random().toString(36).slice(2) + '-' + Date.now().toString(36);
}

/**
 * Return the current AI session id. Generates + persists a fresh one
 * if none exists or the existing one is older than `TTL_HOURS`. Safe
 * to call on every render — reads localStorage (via safeStorage) but
 * never blocks and never throws.
 *
 * Returns the generated/stored id on success, or a fresh non-persisted
 * id if storage is unavailable (memory-only fallback inside
 * safeStorage handles the persistence layer transparently).
 */
export function getOrCreateAiSessionId() {
  const startedAtStr = safeStorage.getString(TIMESTAMP_KEY, '0');
  const startedAt = parseInt(startedAtStr || '0', 10);
  const ageHours = (Date.now() - startedAt) / (1000 * 60 * 60);
  const existing = safeStorage.getString(STORAGE_KEY);
  if (existing && ageHours < TTL_HOURS) {
    return existing;
  }
  const fresh = generateId();
  safeStorage.setString(STORAGE_KEY, fresh);
  safeStorage.setString(TIMESTAMP_KEY, String(Date.now()));
  return fresh;
}

/**
 * Forget the current conversation. Triggers a clean slate on the next
 * `/api/ai/ask` call. Exposed as an escape hatch for the "Reset
 * conversation" affordance we may add to the Command Bar settings —
 * not used by default code paths today.
 *
 * Errors are logged (was a silent catch before, flagged in code
 * review). Best-effort: a failure here just means the next
 * `getOrCreateAiSessionId` call may return the same id, which is
 * fine — TTL eventually rotates it.
 */
export function clearAiSession() {
  try {
    safeStorage.remove(STORAGE_KEY);
    safeStorage.remove(TIMESTAMP_KEY);
  } catch (err) {
    logger.warn('clearAiSession: removal failed (next TTL rotation will recover):', err?.message);
  }
}
